import classnames from 'classnames'
import { Formik } from 'formik'
import React, { useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import Button from '../../components/basics/Button/Button'
import { CheckboxFormik } from '../../components/basics/Checkbox/Checkbox'
import ConfirmDialog from '../../components/basics/ConfirmDialog/ConfirmDialog'
import MessageBox from '../../components/basics/MessageBox/MessageBox'
import { TextInputFormik } from '../../components/basics/TextInput/TextInput'
import OverlayChildPage from '../../components/OverlayChildPage/OverlayChildPage'
import UserSlug from '../../components/slugs/UserSlug/UserSlug'
import { OnboardingDialog } from '../../components/userAndGroupForms/CreateUserForm/OnboardingDialog'
import SetPasswordForm from '../../components/userAndGroupForms/SetPasswordForm/SetPasswordForm'
import UserAvatarForm from '../../components/userAndGroupForms/UserAvatarForm/UserAvatarForm'
import {
  userCanChangeUserStatus,
  userCanDeleteUser,
  userCanEditUser,
  userCanResetUserPassword,
} from '../../state/calculations/permissions/userCan'
import { useUser } from '../../state/entities'
import useDeleteUser from '../../state/mutations/useDeleteUser'
import useRequestPasswordResetForUser from '../../state/mutations/useRequestPasswordResetForUser'
import useUpdateUser from '../../state/mutations/useUpdateUser'
import { useSession } from '../../state/session'
import useRoutes from '../../state/useRoutes'
import getErrorMesssage from '../../utilities/getErrorMesssage'
import useDialog from '../../utilities/useDialog'
import ManageUserPageAchievements from './ManageUserPageAchievements'
import ManageUserPageGroupMemberships from './ManageUserPageGroupMemberships'
import ManageUserPageLearningAssignments from './ManageUserPageLearningAssignments'
import styles from './ManageUserPage.module.scss'
import ManageUserPageTrainerAssignments from './ManageUserPageTrainerAssignments'
import ManageUserPageLearningPathAssignments from './ManageUserPageLearningPathAssignments'

/**********************************************************************
 * ManageUserPage
 **********************************************************************/
export default React.memo(function ManageUserPage() {
  const { t } = useTranslation()
  const showDialog = useDialog()
  const { user: currentUser } = useSession()

  const { uuid } = useParams()

  const updateUser = useUpdateUser()

  const { user } = useUser(uuid)

  // request password reset
  const requestPasswordResetForUser = useRequestPasswordResetForUser()
  const handleRequestPasswordReset = useCallback(async () => {
    const result = await showDialog((close) => (
      <ConfirmDialog close={close} yesLabel={t('generic cta.yes')} noLabel={t('generic cta.cancel')}>
        <div>
          <Trans t={t} i18nKey="login password.should the password for {user} be reset">
            <UserSlug user={user} />
          </Trans>
        </div>
      </ConfirmDialog>
    ))

    if (!result) {
      return
    }

    try {
      const { url, password } = await requestPasswordResetForUser.mutateAsync({ userUuid: uuid! })

      if (url) {
        await showDialog((close) => (
          <MessageBox close={close}>
            <OnboardingDialog
              username={user.username!}
              password={password!}
              link={url}
              name={`${user.firstname} ${user.lastname}`}
            />
          </MessageBox>
        ))
      } else {
        await showDialog((close) => (
          <MessageBox close={close}>
            <p>{t('login password.Password reset email sent to user')}</p>
          </MessageBox>
        ))
      }
    } catch (err) {
      await showDialog((close) => (
        <MessageBox close={close}>
          <p>{String(err)}</p>
        </MessageBox>
      ))
    }
  }, [uuid, user])

  // set new password
  const handleSetNewPassword = useCallback(async () => {
    await showDialog<void>(
      (close) => <SetPasswordForm onAbort={close} onSuccess={close} emailOrUsername={user.email! || user.username!} />,
      () => undefined
    )
  }, [user])

  // delete
  const deleteUser = useDeleteUser()
  const handleDeleteUser = useCallback(async () => {
    const result = await showDialog((close) => (
      <ConfirmDialog close={close} yesLabel={t('generic cta.yes')} noLabel={t('generic cta.cancel')}>
        <div>
          <Trans t={t} i18nKey="manage user.should {user} be deleted">
            <UserSlug user={user} />
          </Trans>
        </div>
      </ConfirmDialog>
    ))

    if (!result) {
      return
    }

    try {
      await deleteUser.mutateAsync({ userUuid: user.uuid })

      await showDialog((close) => (
        <MessageBox close={close}>
          <p>{t('manage user.user was deleted')}</p>
        </MessageBox>
      ))
    } catch (err) {
      await showDialog((close) => (
        <MessageBox close={close}>
          <p>{getErrorMesssage({ t }, err)}</p>
        </MessageBox>
      ))
    }
  }, [user])

  if (!uuid || user.error || user.isAnonymous) {
    throw new Error('User not found')
  }

  const isDeleted = Boolean(user.deletedAt)

  const canEdit = userCanEditUser(currentUser, user)
  const canChangeStatus = userCanChangeUserStatus(currentUser, user)
  const canResetPassword = userCanResetUserPassword(currentUser, user)
  const canDelete = userCanDeleteUser(currentUser, user)

  const usernameSuffix = user.username ? user.username.split('#')[1] : currentUser?.client.code

  return (
    <article className={classnames(styles.ManageUserPage, { skeleton: user.isDummy })}>
      <div className={styles.detailforms}>
        <section className={styles.avatarForm}>
          <UserAvatarForm user={user} />
        </section>

        <section className={styles.userform}>
          <Formik
            enableReinitialize={true}
            initialValues={{
              firstname: user.firstname,
              lastname: user.lastname,
              position: user.position,
              username: user.username ? user.username.split('#')[0] : '',
              email: user.email,
            }}
            validate={(values) => {
              const errors: any = {}
              if (!values.email && !values.username) {
                errors.email = t('errors.username or email required')
                errors.username = t('errors.username or email required')
              }
              return errors
            }}
            onSubmit={async (values, { setStatus, resetForm }) => {
              try {
                await updateUser.mutateAsync({
                  uuid,
                  isActive: user.status === 'active',
                  role: user.role,
                  ...values,
                  username: values.username ? `${values.username}#${usernameSuffix}` : undefined,
                })
                resetForm()
              } catch (err) {
                setStatus(err)
              }
            }}
          >
            {(props) => (
              <form noValidate onSubmit={props.handleSubmit}>
                <TextInputFormik label={t('user.firstname')} name="firstname" required readOnly={!canEdit} />
                <TextInputFormik label={t('user.lastname')} name="lastname" required readOnly={!canEdit} />
                {(canEdit || props.values.position) && (
                  <TextInputFormik
                    className={styles.position}
                    label={t('user.field position')}
                    name="position"
                    readOnly={!canEdit}
                  />
                )}
                {(canEdit || props.values.username) && (
                  <TextInputFormik
                    label={t('user.field username')}
                    name="username"
                    readOnly={!canEdit}
                    after={<span>#{usernameSuffix}</span>}
                  />
                )}
                {(canEdit || props.values.email) && (
                  <TextInputFormik label={t('user.emailAddress')} name="email" type="email" readOnly={!canEdit} />
                )}
                {canEdit && (
                  <div className={styles.buttons}>
                    <Button type="submit" loading={props.isSubmitting} small filled>
                      {t('generic cta.save')}
                    </Button>
                  </div>
                )}
              </form>
            )}
          </Formik>
        </section>

        <section className={styles.activeForm}>
          {canChangeStatus ? (
            <Formik
              enableReinitialize={true}
              initialValues={{
                isActive: user.status === 'active',
              }}
              onSubmit={async (values, { setStatus, resetForm }) => {
                try {
                  await updateUser.mutateAsync({
                    uuid,
                    firstname: user.firstname,
                    lastname: user.lastname,
                    email: user.email,
                    role: user.role,
                    ...values,
                  })
                  resetForm()
                } catch (err) {
                  setStatus(err)
                }
              }}
            >
              {(props) => (
                <form noValidate onSubmit={props.handleSubmit}>
                  <CheckboxFormik
                    toggle
                    onChange={props.submitForm}
                    name="isActive"
                    label={t('user.user_status.active')}
                  />
                </form>
              )}
            </Formik>
          ) : isDeleted ? (
            <div>{t(`user.user_status_is.deleted`)}</div>
          ) : (
            <div>{t(`user.user_status_is.${user.status}`)}</div>
          )}
        </section>

        <section className={styles.actions}>
          {uuid === currentUser?.uuid && (
            <Button small onClick={handleSetNewPassword}>
              {t('login password.cta set password')}
            </Button>
          )}
          {canResetPassword && (
            <>
              <Button small onClick={handleRequestPasswordReset} disabled={!user.username && !user.email}>
                {t('manage user.cta reset password')}
              </Button>
              {user.passwordResetRequired && (
                <span className={styles.passwordResetRequiredHint}>{t('user.password reset required')}</span>
              )}
            </>
          )}
          {canDelete && (
            <Button small color="var(--text-error)" onClick={handleDeleteUser}>
              {t('manage user.cta delete')}
            </Button>
          )}
        </section>
      </div>

      <ManageUserPageGroupMemberships uuid={uuid} />

      <ManageUserPageTrainerAssignments uuid={uuid} />

      <ManageUserPageLearningPathAssignments uuid={uuid} />

      <ManageUserPageLearningAssignments uuid={uuid} />

      <ManageUserPageAchievements uuid={uuid} />

      <OverlayChildPage />
    </article>
  )
})

export function ManageUserPageBreadcrumb() {
  const { uuid } = useParams()
  const { user } = useUser(uuid)
  const routes = useRoutes()
  return <Link to={routes.manageUser.url({ uuid: uuid! })}>{`${user.firstname} ${user.lastname}`}</Link>
}
